/**
 * Selling things back.
 *
 * The hold fills up. Until now the only way out of it was fitting a part to the
 * ship or throwing line at a wall, so a trader who would not buy anything made
 * cargo a one-way valve — and a delivery item that had expired could never be
 * got rid of at all.
 *
 * ── Why scarcity sets the price ─────────────────────────────────────────────
 *
 * Sixty per cent if the trader already stocks it, eighty if not. That is a
 * convenience feature with a trade mechanic hidden inside it: the same crate is
 * worth a third more at a station that has none, so "where do I sell this" has
 * an answer that is about the world rather than about a number. It costs
 * nothing to implement and it is the whole of why the split exists.
 *
 * ── And why it cannot be farmed ─────────────────────────────────────────────
 *
 * Buying costs `base × TRADER_MARKUP`, which is 1.2. Selling returns at most
 * 0.8 of value. A round trip is therefore 96% at its very best — a reliable
 * small loss, which is what a spread should be. There is no route through this
 * that ends up ahead.
 */
import { COMPONENT_PRICES, JOINT_PRICE, RADIO_PULSE_BEACON, WARP_OVERDRIVE_CORE, linePrice, } from './trader.js';
import { trinketById, trinketSellPrice } from './trinkets.js';
/** Paid when the trader already has one on the shelf. */
export const SELL_IN_STOCK = 0.6;
/** Paid when they do not, which is the point of carrying it here. */
export const SELL_SCARCE = 0.8;
/**
 * What one of these is worth before the spread.
 *
 * Lines, parts and joints are worth what they are made of, so the figure comes
 * from the same tables the trader prices its shelves from — one source, so a
 * price rise cannot leave selling stale. A resource carries its own value,
 * which for a delivery item is zero.
 */
export function itemValue(item) {
    switch (item.kind) {
        case 'line':
            return linePrice(item.length ?? 0);
        case 'component':
            return item.type ? COMPONENT_PRICES[item.type] : 0;
        case 'joint':
            return JOINT_PRICE;
        default:
            /*
             * A resource carries its own value — except the one resource the game
             * actually sells, which is worth what the shop charges for it.
             *
             * The stored figure was only set by the buy path, so a core that arrived
             * any other way — an admin grant, a save from before the field existed —
             * read as worthless. A known good with a known price should not depend on
             * how it got into the hold.
             */
            if (item.id === WARP_OVERDRIVE_CORE.id)
                return WARP_OVERDRIVE_CORE.price;
            if (item.id === RADIO_PULSE_BEACON.id)
                return RADIO_PULSE_BEACON.price;
            return item.value ?? 0;
    }
}
/**
 * What a trader will hand over for one, given whether they stock it.
 *
 * Rounded to whole linelits, because a purse that reads in fractions invites
 * arithmetic nobody wants to do.
 */
/**
 * What this trader pays for one.
 *
 * ── Trinkets are the exception, and they are the exception on purpose ───────
 *
 * Everything else is worth what it is made of, so `itemValue` can answer without
 * being told where it is standing. A trinket is worth nothing except what
 * somebody will pay, so the *station* is the price — and a function that did not
 * take one could only ever return a number that was wrong somewhere.
 *
 * `where` is therefore optional for the rest of the hold and required in
 * practice for trinkets: without it there is no honest figure to give, and zero
 * is the honest way to say so rather than a base price nobody is offering.
 *
 * The scarcity split does not apply to them either. Sixty per cent against
 * eighty is a rule about a trader already having one, which is a fact about
 * *supply*; a trinket's price is a fact about taste, and stacking a second
 * modifier on top would blur the one signal the mechanic runs on.
 */
export function sellPrice(item, inStock, where) {
    if (item.kind === 'trinket') {
        // `trinket` rather than `id`: the id identifies the *lot*, price and all.
        const trinket = trinketById(item.trinket ?? item.id);
        if (!trinket || !where)
            return 0;
        return trinketSellPrice(where.seed, where.stationId, trinket);
    }
    return Math.round(itemValue(item) * (inStock ? SELL_IN_STOCK : SELL_SCARCE));
}
/**
 * Whether a trader would take this at all.
 *
 * Everything is sellable, including a dead delivery — that is how you get an
 * expired crate out of the hold. It simply pays nothing, which is the honest
 * price of a thing nobody asked for.
 */
export function sellable(item) {
    return item.count > 0;
}
/**
 * Sell several of a stack.
 *
 * The count is chosen deliberately — a spinner and a total before the button,
 * rather than a row that empties itself on a mis-click. That is why this takes
 * a number instead of being called in a loop: the player decides once, sees
 * what it pays, and commits once.
 */
export function sellSome(cargo, itemId, count, inStock, 
/** Where the sale is happening. Only trinkets care, and they care entirely. */
where) {
    const item = cargo.find((held) => held.id === itemId);
    if (!item || !sellable(item))
        return null;
    const sold = Math.max(0, Math.min(Math.floor(count), item.count));
    if (sold === 0)
        return null;
    return {
        cargo: cargo
            .map((held) => (held.id === itemId ? { ...held, count: held.count - sold } : held))
            .filter((held) => held.count > 0),
        paid: sellPrice(item, inStock, where) * sold,
        restocked: item.mission === undefined && sellPrice(item, inStock, where) > 0,
    };
}
/** Sell exactly one. A convenience over `sellSome`, and the common case. */
export function sellOne(cargo, itemId, inStock) {
    const item = cargo.find((held) => held.id === itemId);
    if (!item || !sellable(item))
        return null;
    return sellSome(cargo, itemId, 1, inStock);
}
//# sourceMappingURL=selling.js.map